import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import { Session } from '@/types/working-model';
import { EditorSizes } from '@/types/template-layouts';
import { useScreenMediaQuery } from '@/utils/use-screen-media-query';
import { isRichTextEmpty } from '@/utils/is-rich-text-empty';
import { normalizeQuillSpaces } from '@general-ui/editable-text/normalize-quill-spaces';

import '@general-ui/editable-text/quill-editor.scss';
import './persistent-text-box.scss';

/** Returns the sanitized html for the session's persistent text box, or null when it is off or empty */
export const usePersistentTextBoxHtml = (session: Session | undefined, language: string): string | null => {
	const persistentTextBox = session?.layout?.persistent_text_box;

	return useMemo(() => {
		if (!persistentTextBox?.enabled) return null;

		const content = persistentTextBox.content;
		const html = (content?.[language] as string | undefined) || content?.base;
		if (!html || isRichTextEmpty(html)) return null;

		return DOMPurify.sanitize(normalizeQuillSpaces(html), { ADD_ATTR: ['target'] });
	}, [persistentTextBox, language]);
};

/** Below 1024px the side panel is not rendered, so the text box moves below the video player */
export const useIsPersistentTextBoxBelowPlayer = (editorSize?: EditorSizes | string): boolean => {
	const { isLessThan1024 } = useScreenMediaQuery();
	return isLessThan1024 || editorSize === EditorSizes.mobile || editorSize === EditorSizes.tablet;
};

type PersistentTextBoxProps = {
	html: string;
	belowPlayer?: boolean;
};

const PersistentTextBox = ({ html, belowPlayer }: PersistentTextBoxProps): JSX.Element => (
	<div className={classNames('persistent-text-box quill-content', { 'below-player': belowPlayer })}>
		<div dangerouslySetInnerHTML={{ __html: html }} />
	</div>
);

export default PersistentTextBox;
